
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function CostCategories() {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Cost Categories - Negotrix | Commercial Confidence & Vendor Outcomes';
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const categories = [
    {
      num: '01',
      title: 'SaaS & Software',
      improvement: '25–40%',
      desc: 'Improve commercial outcomes by identifying unused licenses, consolidating duplicate tools, and right-sizing enterprise agreements.',
      points: ['License audit & contract strategy', 'Contract renegotiation', 'Tool consolidation analysis'],
      path: '/saas-software'
    },
    {
      num: '02',
      title: 'Infrastructure & Cloud',
      improvement: '30–50%',
      desc: 'Reduce cloud waste, right-size instance sizing, and negotiate better AWS/Azure/GCP commitments.',
      points: ['AWS/Azure cost analysis', 'Reserved instance strategy', 'Architecture efficiency review'],
      path: '/infrastructure-cloud'
    },
    {
      num: '03',
      title: 'Marketing & Advertising',
      improvement: '30–50%',
      desc: 'Eliminate wasted ad spend, improve channel strategy, and strengthen ROI tracking across all marketing activities.',
      points: ['Ad spend analysis & waste elimination', 'Channel performance strategy', 'MarTech stack consolidation'],
      path: '/marketing-advertising'
    },
    {
      num: '04',
      title: 'Vendor Contracts',
      improvement: '15–25%',
      desc: 'Renegotiate terms, consolidate suppliers, and leverage benchmarking data for better vendor agreements.',
      points: ['Contract analysis & benchmarking', 'Supplier consolidation', 'Negotiation & renewal strategy'],
      path: '/vendor-contracts'
    }
  ];

  return (
    <div className={`min-h-screen bg-cipher-black text-white transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <Header />

      {/* Hero */}
      <section className="pt-40 pb-24 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-6">Our Expertise</h2>
          <h1 className="text-[9vw] leading-[0.9] font-black tracking-tighter mb-12">
            <span className="text-white">Four Categories.</span><br />
            <span className="text-cipher-hypergreen">One Goal.</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl">
            We systematically improve the 20–30% of commercial value that gets lost as companies scale.
            <span className="text-white ml-2 font-medium">No upfront fees. Pay only on results.</span>
          </p>
        </div>
      </section>

      {/* Category Rows */}
      <section className="pb-32 bg-cipher-black">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="border-t border-gray-800">
            {categories.map((cat) => (
              <div
                key={cat.num}
                className="group border-b border-gray-800 py-16 hover:bg-cipher-dark transition-colors cursor-pointer px-6 -mx-6"
                onClick={() => navigate(cat.path)}
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  <div className="lg:col-span-1 text-gray-600 font-black text-2xl">{cat.num}</div>
                  <div className="lg:col-span-4">
                    <div className="text-3xl lg:text-5xl font-black group-hover:text-cipher-hypergreen transition-colors mb-4">
                      {cat.title}
                    </div>
                    <div className="inline-block px-4 py-2 bg-cipher-hypergreen/10 border border-cipher-hypergreen/30 rounded-full text-cipher-hypergreen text-sm font-bold">
                      {cat.improvement} commercial improvement
                    </div>
                  </div>
                  <div className="lg:col-span-4">
                    <p className="text-gray-400 text-lg leading-relaxed mb-6">{cat.desc}</p>
                    <ul className="space-y-2">
                      {cat.points.map((p, i) => (
                        <li key={i} className="flex items-center space-x-3 text-gray-500 text-sm">
                          <span className="w-1.5 h-1.5 bg-cipher-hypergreen rounded-full flex-shrink-0"></span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="lg:col-span-3 flex items-center justify-end">
                    <span className="text-gray-600 group-hover:text-cipher-hypergreen transition-colors text-4xl">→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-cipher-dark border-t border-gray-900">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-16">Our Approach</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                val: 'No',
                label: 'Upfront Fees'
              },
              {
                val: 'Only',
                label: 'On Results'
              },
              {
                val: 'Direct',
                label: 'Vendor Negotiation'
              }
            ].map((s, i) => (
              <div key={i} className="p-8 bg-cipher-black rounded-[2rem] border border-gray-800">
                <div className="text-5xl lg:text-7xl font-black text-cipher-hypergreen mb-4">{s.val}</div>
                <div className="text-sm tracking-widest uppercase text-gray-500 font-bold">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 lg:py-48 bg-cipher-black border-t border-gray-800">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-[8vw] leading-[0.9] font-black tracking-tighter text-white mb-12">
              Ready to <br /><span className="text-cipher-hypergreen">build commercial confidence?</span>
          </h2>
          <button
            onClick={() => navigate('/contact')}
            className="inline-flex items-center justify-center px-10 py-5 text-lg tracking-wide font-bold uppercase rounded-full text-cipher-black bg-cipher-hypergreen hover:bg-white transition-all duration-300"
          >
             Start Your Commercial Review
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
